import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, Settings, Zap, Download, Search, CheckSquare, Square, Bot, Code, Sparkles } from 'lucide-react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { generateWxccFunction, isWxccFunctionBackendConfigured } from '../api_scripts/wxccFunctionClient';
import { callQualityJudge } from '../../../shared/services/qualityJudgeService';
import JudgeProgressModal from '../../../components/JudgeProgressModal';

import getQueues from '..//api_scripts/get/getQueues';
import getBusinessHours from '..//api_scripts/get/getBusinessHours';
import getholidayList from '..//api_scripts/get/getHolidayList';
import getoverridesList from '..//api_scripts/get/getOverrideList';
import getEntryPoint from '..//api_scripts/get/getEntryPoints';
import getCADvariable from '..//api_scripts/get/getCADVars';
import getskilldefinitionsList from '..//api_scripts/get/getSkillDefinitions';
import getUsersList from '..//api_scripts/get/getUsersList';
import getAudioFiles from '..//api_scripts/get/getAudioFiles';

// Compact standards object sent to the quality-judge Lambda. Mirrors
// lambda/ccutils/wxcc-function-generator/standards/wxcc-functions.json
// (the runtime + behavior + constraints sections). Kept inline here so the
// SPA doesn't need an extra fetch on every generate — the standards are
// stable and tiny (~2KB).
const WXCC_FUNCTION_STANDARDS_FOR_JUDGE = {
  product: 'Cisco WxCC Flow Designer Functions',
  runtimeFacts: {
    language: 'JavaScript (Node.js 22.x)',
    executionTimeoutSec: 5,
    memoryMb: 128,
    maxLinesOfCode: 5000,
    maxInputVariables: 10,
    maxOutputVariables: 1,
    outputType: 'single JSON object (parsed via JSONPath on the flow side)',
    libraries: 'Node 22 stdlib + globals only. No custom modules.',
    logging: 'console.log dropped at runtime. For debug, push strings onto response.data.debug[] and return them.'
  },
  runtimeContract: {
    signature: 'export const handle = async (request, response) => { ... }',
    inputAccess: 'request.inputs.<name>',
    outputSetting: 'response.data = {...}; response.statusCode = <number>; return response;'
  },
  responseStatusCodeConvention: {
    200: 'success',
    400: 'caller error (missing/malformed input)',
    404: 'lookup miss',
    500: 'unexpected error inside catch'
  },
  behaviorRules: {
    errorHandling: 'try { ... } catch (error) { response.statusCode = 500; response.data = { error: <summary>, details: error.message }; } around the whole body.',
    inputGuards: 'Every request.inputs.X validated with early `if (!X) { response.statusCode = 400; return response; }` BEFORE first use.',
    lookupNotFound: 'Every .find() has a not-found check returning 404. No .id access on undefined matches.',
    patternMatching: 'Case-insensitive compares (.toLowerCase()), anchored regex for suffix stripping (/_EP$/i, /_Q$/i, /_CCB_Status$/i).',
    determinism: 'No Date.now(), Math.random(), env reads. Same inputs → same outputs.'
  },
  constraints: {
    noMarkdown: 'Raw JavaScript only — no ```js fences, no commentary.',
    noCustomModules: 'No require/import of third-party modules. Native fetch only for outbound HTTP.',
    completeDatasetInclusion: 'Provided entities are all embedded in code (or template marks where to paste the rest).'
  }
};

// Helper function to analyze naming patterns (moved to top to avoid hoisting issues)
const analyzeNamingPatterns = (entities) => {
  const names = entities.map(e => e.name);
  const prefixes = [...new Set(names.map(n => n.split('_')[0]))];
  const suffixes = [...new Set(names.map(n => n.split('_').pop()))];
  const commonPatterns = [];
  
  // Find common patterns
  if (prefixes.length < 10) commonPatterns.push(`Common prefixes: ${prefixes.join(', ')}`);
  if (suffixes.length < 10) commonPatterns.push(`Common suffixes: ${suffixes.join(', ')}`);
  
  return commonPatterns;
};


// Placeholder for getAgents - replace with actual API call if needed
const getAgents = async (entityType, orgId, accessToken, setProgress) => {
  setProgress("info", "Fetching agents (placeholder - no real API connected)...");
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading
  setProgress("warning", "Agents API not yet implemented. Returning empty list.");
  return [];
};

// Placeholder for getSkills - replace with actual API call if needed
const getSkills = async (entityType, orgId, accessToken, setProgress) => {
  setProgress("info", "Fetching skills (placeholder - no real API connected)...");
  await new Promise(resolve => setTimeout(resolve, 500)); // Simulate loading
  setProgress("warning", "Skills API not yet implemented. Returning empty list.");
  return [];
};

// Enhanced error categorization
const categorizeError = (error) => {
  const message = (error.message || '').toLowerCase();
  if (message.includes('unauthorized') || message.includes('403') || message.includes('401')) {
    return { type: 'auth', message: '❌ Auth failed. Sign in again and retry.' };
  } else if (message.includes('quota') || message.includes('429') || message.includes('throttl')) {
    return { type: 'quota', message: '⏳ Bedrock quota exceeded. Try again in a few seconds.' };
  } else if (message.includes('timeout') || message.includes('network')) {
    return { type: 'network', message: '⏱️ Request timed out. Try with smaller dataset or simpler prompt.' };
  } else if (message.includes('too large') || message.includes('token')) {
    return { type: 'size', message: '📊 Request too large. Try with fewer entities or simpler prompt.' };
  } else {
    return { type: 'generic', message: `❌ Generation failed: ${error.message}` };
  }
};

// Enhanced entity relationship analysis
const analyzeEntityRelationships = (entities) => {
  const relationships = {};
  
  Object.entries(entities).forEach(([type, entityList]) => {
    entityList.forEach(entity => {
      const baseName = entity.name.replace(/_(EP|Q|CCB_Status)$/i, '');
      if (!relationships[baseName]) {
        relationships[baseName] = {
          entryPoint: null,
          queue: null,
          ccbStatus: null,
          confidence: 0,
          baseName
        };
      }
      
      if (entity.name.toLowerCase().endsWith('_ep')) {
        relationships[baseName].entryPoint = entity;
        relationships[baseName].confidence += 0.4;
      } else if (entity.name.toLowerCase().endsWith('_q')) {
        relationships[baseName].queue = entity;
        relationships[baseName].confidence += 0.4;
      } else if (entity.name.toLowerCase().includes('ccb_status')) {
        relationships[baseName].ccbStatus = entity;
        relationships[baseName].confidence += 0.2;
      }
    });
  });
  
  return Object.entries(relationships)
    .filter(([_, rel]) => rel.confidence > 0.6)
    .sort((a, b) => b[1].confidence - a[1].confidence)
    .slice(0, 10); // Top 10 relationships
};


const EnhancedQueueMapping = ({ onBackToTools, authData }) => {
  // Existing state - Modified for multiple entity types
  const [allEntities, setAllEntities] = useState({}); // Store entities by type: { queues: [...], holidays: [...] }
  const [selectedEntities, setSelectedEntities] = useState({}); // Store selections by type: { queues: {...}, holidays: {...} }
  const [selectedEntityTypes, setSelectedEntityTypes] = useState(['queues']); // Allow multiple entity types
  const [loading, setLoading] = useState(false);
  const [loadingEntityTypes, setLoadingEntityTypes] = useState(new Set());
  const [progress, setProgress] = useState([]);
  const [generatedJson, setGeneratedJson] = useState(null);
  const [functionNameInput, setFunctionNameInput] = useState('Enhanced_Function');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Enhanced state
  const [activeTab, setActiveTab] = useState('mapping');
  const [currentEntityType, setCurrentEntityType] = useState('queues'); // Current type being viewed/filtered
  const [entityRelationships, setEntityRelationships] = useState([]);

  // Quality-judge advisory state. judgeRunIdRef guards against stale
  // responses if the user regenerates while a previous judge is still
  // in flight. lastGeneratePayloadRef holds the prompt + entityData used
  // for the most recent submit so handleRegenerate can refire with
  // priorJudgeFindings spread in.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);
  const lastGeneratePayloadRef = useRef(null);

  const { getApiToken } = useAuth();
  const backendConfigured = isWxccFunctionBackendConfigured();
  const [llmPrompt, setLLMPrompt] = useState('I want you to build a mapping function.\n\nThe primary input will be Queue Entry Point ID, the object that it should return when matching the ID should be:\n- Entry Point Name\n- Queue ID\n- Queue Name\n- CCB Status ID\n\nThe Queue Name should be matched to the Entry Point Name; leverage comparison logic to find what Entry Point matches what Queue. The CCB Status should also be matched using similar logic.\n\nUse intelligent name matching to associate Entry Points with Queues and CCB Status variables based on similar patterns, prefixes, or logical naming conventions.');
  const [llmGeneratedCode, setLLMGeneratedCode] = useState('');
  const [isLLMGenerating, setIsLLMGenerating] = useState(false);
  const [advancedSettings, setAdvancedSettings] = useState(false);
  const [sourceCode, setSourceCode] = useState(`export const handle = async (request, response) => {
  // Your JavaScript code here
  // Access inputs via request.inputs
  // Set outputs via response.data = {...}
  
  // Example:
  const inputValue = request.inputs.inputName;
  
  response.data = {
    message: "Hello from your custom function!",
    input: inputValue
  };
  
  return response;
};`);
  
  const logContainerRef = useRef(null);
  const { org_id, access_token } = authData;

  // Updated entityTypes to use actual getQueues and placeholder functions
  const entityTypes = {
    queues: { label: 'Queues', fetchFunction: getQueues },
    bushours: { label: 'Business Hours', fetchFunction: getBusinessHours },
    holidays: { label: 'Holidays', fetchFunction: getholidayList },
    overrides: { label: 'Overrides', fetchFunction: getoverridesList },
    entrypoints: { label: 'Entry Points', fetchFunction: getEntryPoint },
    cadvar: { label: 'Global Variables', fetchFunction: getCADvariable },
    skilldefinitions: { label: 'Skill Definitions', fetchFunction: getskilldefinitionsList },
    users: { label: 'Users', fetchFunction: getUsersList },
    audiofiles: { label: 'Audio Files', fetchFunction: getAudioFiles }
  };

  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  const updateProgress = (type, message) => {
    setProgress((prev) => {
      const newProgress = [
        ...prev,
        { type, message, timestamp: new Date().toLocaleTimeString() },
      ];
      return newProgress;
    });
    setTimeout(scrollToBottom, 0);
  };

  const getMessageStyle = (type) => {
    const styles = {
      info: { color: '#2196f3' },
      success: { color: '#4caf50' },
      warning: { color: '#ff9800' },
      error: { color: '#f44336' },
      default: { color: '#333' }
    };
    return styles[type] || styles.default;
  };

  // Fetch entities for selected entity types
  useEffect(() => {
    const fetchAllSelectedEntityTypes = async () => {
      if (!org_id || !access_token) return;
      
      const typesToFetch = selectedEntityTypes.filter(type => !allEntities[type]);
      
      if (typesToFetch.length === 0) return;
      
      for (const entityType of typesToFetch) {
        setLoadingEntityTypes(prev => new Set(prev).add(entityType));
        updateProgress("info", `Fetching ${entityTypes[entityType].label.toLowerCase()}...`);
        
        try {
          const fetchFunction = entityTypes[entityType].fetchFunction;
          const fetchedEntities = await fetchFunction(null, org_id, access_token, updateProgress);
          
          // Update allEntities with new data
          setAllEntities(prev => ({
            ...prev,
            [entityType]: fetchedEntities
          }));
          
          // Initialize selections for this entity type as unselected
          const initialSelection = fetchedEntities.reduce((acc, entity) => {
            acc[entity.id] = false;
            return acc;
          }, {});
          
          setSelectedEntities(prev => ({
            ...prev,
            [entityType]: initialSelection
          }));
          
          updateProgress("success", `Found ${fetchedEntities.length} ${entityTypes[entityType].label.toLowerCase()}.`);
        } catch (error) {
          updateProgress("error", `Failed to fetch ${entityTypes[entityType].label.toLowerCase()}: ${error.message}`);
        } finally {
          setLoadingEntityTypes(prev => {
            const newSet = new Set(prev);
            newSet.delete(entityType);
            return newSet;
          });
        }
      }
    };

    fetchAllSelectedEntityTypes();
  }, [selectedEntityTypes, org_id, access_token]);

  const handleEntityToggle = useCallback((entityId) => {
    setSelectedEntities((prevSelected) => ({
      ...prevSelected,
      [currentEntityType]: {
        ...prevSelected[currentEntityType],
        [entityId]: !prevSelected[currentEntityType]?.[entityId]
      }
    }));
  }, [currentEntityType]);

  const handleSelectAll = () => {
    const currentEntities = allEntities[currentEntityType] || [];
    const allSelected = currentEntities.reduce((acc, entity) => {
      acc[entity.id] = true;
      return acc;
    }, {});
    
    setSelectedEntities(prev => ({
      ...prev,
      [currentEntityType]: allSelected
    }));
  };

  const handleDeselectAll = () => {
    setSelectedEntities(prev => ({
      ...prev,
      [currentEntityType]: {}
    }));
  };

  // Filter management functions
  const addFilter = () => {
    const newFilter = {
      id: Date.now(),
      type: 'startsWith',
      value: '',
      field: 'name' // can be 'name' or 'id'
    };
    setFilters([...filters, newFilter]);
    setShowFilters(true);
  };

  const updateFilter = (filterId, key, value) => {
    setFilters(filters.map(filter => 
      filter.id === filterId ? { ...filter, [key]: value } : filter
    ));
  };

  const removeFilter = (filterId) => {
    setFilters(filters.filter(filter => filter.id !== filterId));
  };

  const clearAllFilters = () => {
    setFilters([]);
    setSearchTerm('');
  };

  // Advanced filtering logic
  const applyFilters = (entity) => {
    // First apply basic search
    const matchesSearch = !searchTerm || 
      entity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entity.id.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // Then apply advanced filters
    if (filters.length === 0) return true;

    return filters.every(filter => {
      if (!filter.value) return true; // Skip empty filters

      const fieldValue = (filter.field === 'name' ? entity.name : entity.id).toLowerCase();
      const filterValue = filter.value.toLowerCase();

      switch (filter.type) {
        case 'startsWith':
          return fieldValue.startsWith(filterValue);
        case 'endsWith':
          return fieldValue.endsWith(filterValue);
        case 'contains':
          return fieldValue.includes(filterValue);
        case 'notContains':
          return !fieldValue.includes(filterValue);
        case 'equals':
          return fieldValue === filterValue;
        case 'regex':
          try {
            return new RegExp(filter.value, 'i').test(fieldValue);
          } catch (e) {
            return true; // Invalid regex, don't filter
          }
        default:
          return true;
      }
    });
  };

  const currentEntities = allEntities[currentEntityType] || [];
  const filteredEntities = useMemo(() => 
    currentEntities.filter(applyFilters), 
    [currentEntities, searchTerm, filters]
  );

  // Helper to select all filtered entities
  const handleSelectAllFiltered = () => {
    const filteredSelection = filteredEntities.reduce((acc, entity) => {
      acc[entity.id] = true;
      return acc;
    }, {});
    
    setSelectedEntities(prevSelected => ({
      ...prevSelected,
      [currentEntityType]: {
        ...prevSelected[currentEntityType],
        ...filteredSelection
      }
    }));
  };

  // Helper to deselect all filtered entities
  const handleDeselectAllFiltered = () => {
    const filteredIds = filteredEntities.map(entity => entity.id);
    
    setSelectedEntities(prevSelected => {
      const currentSelected = { ...prevSelected[currentEntityType] };
      filteredIds.forEach(id => {
        delete currentSelected[id];
      });
      
      return {
        ...prevSelected,
        [currentEntityType]: currentSelected
      };
    });
  };

  // Helper function to build entity data for LLM context
  const buildEntityDataForLLM = () => {
    let contextData = "";
    let totalSelectedCount = 0;
    
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);
      
      if (selectedEntityData.length > 0) {
        totalSelectedCount += selectedEntityData.length;
        const entityDataString = selectedEntityData.map(entity => {
          return `  - ID: ${entity.id}, Name: "${entity.name}"${entity.description ? `, Description: "${entity.description}"` : ''}`;
        }).join('\n');
        
        contextData += `\n\nSelected ${entityTypes[entityType].label} Data (${selectedEntityData.length} items):\n${entityDataString}`;
      }
    });
    
    if (totalSelectedCount === 0) {
      return "";
    }

    return `${contextData}\n\nUse this data to create mappings, lookups, or relationships in your function. You have access to ${totalSelectedCount} total selected entities across ${selectedEntityTypes.length} entity types.`;
  };

  // Helper function to get all entity types data for cross-entity relationships
  const getAllEntityDataForLLM = async () => {
    const allEntityData = { ...allEntities }; // Start with already loaded entities
    
    // Fetch data from entity types that haven't been loaded yet
    for (const [key, type] of Object.entries(entityTypes)) {
      if (!allEntityData[key]) {
        try {
          updateProgress("info", `Fetching ${type.label} for LLM context...`);
          const entityData = await type.fetchFunction(null, org_id, access_token, updateProgress);
          allEntityData[key] = entityData;
        } catch (error) {
          updateProgress("warning", `Could not fetch ${type.label} for LLM context: ${error.message}`);
        }
      }
    }
    
    return allEntityData;
  };

  // Fires the judge AFTER a successful generation. Same advisory-grade
  // pattern as SIT importers + AgentStudio: silently drops the badge on
  // failure (judge is advisory; never blocks the result panel).
  const startJudge = async ({ prompt, entityData, generatedCode }) => {
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      // Embed the standards inline so the judge can grade "did the code
      // follow the same rules the generator was told to apply?" The
      // standards are static so the SPA can ship a compact copy.
      const data = await callQualityJudge({
        kind: 'wxcc-function',
        enginerPrompt: prompt,
        entityData,
        generatedCode,
        standards: WXCC_FUNCTION_STANDARDS_FOR_JUDGE
      });
      if (judgeRunIdRef.current !== runId) return;
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  const generateIntelligentLLMFunction = async (priorJudgeFindings) => {
    if (!llmPrompt.trim()) {
      updateProgress("error", "Please describe what you want your function to do.");
      return;
    }

    if (!backendConfigured) {
      updateProgress("error", "CC Utils API URL is not configured. Set REACT_APP_CCUTILS_API_BASE_URL.");
      return;
    }

    setIsLLMGenerating(true);
    setQualityScore(null);
    setQualityFindings([]);
    updateProgress("info", "🤖 Analyzing your requirements and entity data...");

    try {
      // Prepare entity data for intelligent processing
      const entityDataForLLM = {};
      let totalEntities = 0;

      selectedEntityTypes.forEach(entityType => {
        const typeEntities = allEntities[entityType] || [];
        const typeSelections = selectedEntities[entityType] || {};
        const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);

        if (selectedEntityData.length > 0) {
          entityDataForLLM[entityType] = selectedEntityData;
          totalEntities += selectedEntityData.length;
        }
      });

      // Analyze relationships for better context
      if (totalEntities > 0) {
        const relationships = analyzeEntityRelationships(entityDataForLLM);
        setEntityRelationships(relationships);
        if (relationships.length > 0) {
          updateProgress("info", `🔗 Found ${relationships.length} potential entity relationships`);
        }
      }

      updateProgress("info", `📊 Processing ${totalEntities} entities across ${selectedEntityTypes.length} types via Bedrock Sonnet 4.6…`);

      // Slim the entity data to {id, name} only — same shape the previous
      // Gemini path used; keeps payload small and avoids token-budget hits.
      const slim = {};
      Object.entries(entityDataForLLM).forEach(([type, entities]) => {
        slim[type] = entities.map((e) => ({ id: e.id, name: e.name }));
      });

      lastGeneratePayloadRef.current = { prompt: llmPrompt, entityData: slim };

      const { code: generatedCode, modelId, durationMs } = await generateWxccFunction(
        {
          prompt: llmPrompt,
          entityData: Object.keys(slim).length > 0 ? slim : undefined,
          priorJudgeFindings
        },
        getApiToken
      );

      setLLMGeneratedCode(generatedCode);
      setSourceCode(generatedCode);

      if (totalEntities > 50) {
        updateProgress("success", `🎉 Intelligent function generated by ${modelId} in ${(durationMs / 1000).toFixed(1)}s! Large dataset (${totalEntities} entities) processed with smart chunking.`);
      } else {
        updateProgress("success", `🎉 Complete function generated by ${modelId} in ${(durationMs / 1000).toFixed(1)}s with all ${totalEntities} entities!`);
      }

      // Fire the judge in parallel after the result panel renders.
      startJudge({ prompt: llmPrompt, entityData: slim, generatedCode });
    } catch (error) {
      const categorized = categorizeError(error);
      updateProgress("error", categorized.message);
      // eslint-disable-next-line no-console
      console.error('Bedrock WxCC Function Generator error:', error);
    } finally {
      setIsLLMGenerating(false);
    }
  };

  const handleRegenerateWithFindings = (findings) => generateIntelligentLLMFunction(findings);

  // Simplified - removed the separate advanced function since the main one now handles everything intelligently

  // Helper function to create Entry Point to Queue mapping prompt
  const createEntryPointQueueMappingPrompt = () => {
    const prompt = `I want you to build a mapping function.

The primary input will be Queue Entry Point ID, the object that it should return when matching the ID should be:
- Entry Point Name
- Queue ID  
- Queue Name

The Queue Name should be matched to the Entry Point Name using comparison logic to find what Entry Point matches what Queue.

Example output format:
const epMapping = {
    "807ca344-f437-4f44-996e-6b96814a0307": {
        "epName": "MEDREC_ROI_PhyHosp_EP",
        "queue_id": "24798056-64e0-42f5-8a00-94e076d8792e", 
        "queueName": "MEDREC_ROI_PhysHosp_Q"
    }
}

Use intelligent name matching to associate Entry Points with Queues based on similar patterns, prefixes, or logical naming conventions.`;

    setLLMPrompt(prompt);
    updateProgress("info", "Entry Point to Queue mapping prompt created. You can modify it before generating.");
  };

  // Helper function to use selected entity data in prompt
  const useSelectedEntitiesInPrompt = () => {
    let allSelectedData = [];
    let totalCount = 0;
    let entityTypeSummary = [];
    
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);
      
      if (selectedEntityData.length > 0) {
        totalCount += selectedEntityData.length;
        entityTypeSummary.push(`${selectedEntityData.length} ${entityTypes[entityType].label}`);
        
        allSelectedData.push(`\n${entityTypes[entityType].label}:`);
        selectedEntityData.forEach(entity => {
          allSelectedData.push(`- ID: ${entity.id}, Name: "${entity.name}"`);
        });
      }
    });
    
    if (totalCount === 0) {
      updateProgress("warning", "No entities selected. Please select some entities first.");
      return;
    }

    const entityDataString = allSelectedData.join('\n');
    const summaryString = entityTypeSummary.join(', ');

    const smartPrompt = `Create a mapping function for the following selected entities (${summaryString}):
${entityDataString}

Build a function that takes an input parameter and returns the corresponding mapped data. Use intelligent matching across different entity types and include error handling for non-matching inputs.`;

    setLLMPrompt(smartPrompt);
    updateProgress("success", `Smart prompt created with ${totalCount} selected entities across ${selectedEntityTypes.length} entity types.`);
  };

  // Helper for large datasets - creates pattern-based prompt instead of listing all entities
  const createOptimizedPromptForLargeDataset = () => {
    let totalCount = 0;
    let entityTypeSummary = [];
    let sampleData = [];
    let patterns = [];
    
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);
      
      if (selectedEntityData.length > 0) {
        totalCount += selectedEntityData.length;
        entityTypeSummary.push(`${selectedEntityData.length} ${entityTypes[entityType].label}`);
        
        // For large datasets, show patterns instead of all data
        if (selectedEntityData.length > 20) {
          // Show first 5 and last 5 as samples
          const samples = [
            ...selectedEntityData.slice(0, 5),
            ...selectedEntityData.slice(-5)
          ];
          
          sampleData.push(`\n${entityTypes[entityType].label} (${selectedEntityData.length} total, showing samples):`);
          samples.forEach((entity, index) => {
            const prefix = index === 5 ? '  ... [' + (selectedEntityData.length - 10) + ' more items] ...\n' : '';
            sampleData.push(`${prefix}  - ID: ${entity.id}, Name: "${entity.name}"`);
          });
          
          // Extract naming patterns
          const names = selectedEntityData.map(e => e.name);
          const commonPrefixes = [...new Set(names.map(n => n.split('_')[0]))];
          const commonSuffixes = [...new Set(names.map(n => n.split('_').pop()))];
          
          patterns.push(`\n${entityTypes[entityType].label} Naming Patterns:`);
          if (commonPrefixes.length < 10) patterns.push(`  - Prefixes: ${commonPrefixes.join(', ')}`);
          if (commonSuffixes.length < 10) patterns.push(`  - Suffixes: ${commonSuffixes.join(', ')}`);
          patterns.push(`  - Pattern: Most follow format like "AO_{Department}_{Type}_Q" or similar`);
        } else {
          // Show all data for smaller datasets
          sampleData.push(`\n${entityTypes[entityType].label}:`);
          selectedEntityData.forEach(entity => {
            sampleData.push(`  - ID: ${entity.id}, Name: "${entity.name}"`);
          });
        }
      }
    });
    
    if (totalCount === 0) {
      updateProgress("warning", "No entities selected. Please select some entities first.");
      return;
    }

    const summaryString = entityTypeSummary.join(', ');
    const dataSection = sampleData.join('\n');
    const patternSection = patterns.length > 0 ? `\n\nDetected Patterns:${patterns.join('\n')}` : '';

    const optimizedPrompt = `Create a mapping function for Entry Point ID → Queue mapping with ${summaryString}.

**Task**: Build a function that takes an Entry Point ID as input and returns:
- Entry Point Name  
- Queue ID
- Queue Name

**Matching Logic**: Use intelligent name pattern matching to connect Entry Points to Queues:
- Entry Points typically end with "_EP" 
- Queues typically end with "_Q"
- Remove the suffix and match the base names
- Example: "AO_HEPA_Sched_Q_EP" should match "AO_HEPA_Sched_Q"

**Sample Data**:${dataSection}${patternSection}

**Expected Output Format**:
const epMapping = {
  "entry-point-id-1": {
    "epName": "AO_HEPA_Sched_Q_EP",
    "queue_id": "queue-id-1", 
    "queueName": "AO_HEPA_Sched_Q"
  }
};

**Instructions**:
1. Create mappings for ALL ${totalCount} entities using pattern matching
2. Handle edge cases where names don't follow exact patterns
3. Include error handling for unmapped IDs
4. Use the provided IDs and names from the sample data above`;

    setLLMPrompt(optimizedPrompt);
    updateProgress("success", `Optimized prompt created for large dataset: ${totalCount} entities with pattern-based approach.`);
  };

  // Multi-step approach for large datasets
  const generateDataObjectsAndTemplate = () => {
    let queueData = [];
    let entryPointData = [];
    
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);
      
      if (entityType === 'queues') {
        queueData = selectedEntityData;
      } else if (entityType === 'entrypoints') {
        entryPointData = selectedEntityData;
      }
    });

    if (queueData.length === 0 || entryPointData.length === 0) {
      updateProgress("error", "Please select both Queues and Entry Points for this multi-step approach.");
      return;
    }

    // Step 1: Generate Queue Object
    const queueObject = queueData.reduce((acc, queue) => {
      acc[queue.id] = {
        name: queue.name,
        id: queue.id
      };
      return acc;
    }, {});

    // Step 2: Generate Entry Point Object  
    const entryPointObject = entryPointData.reduce((acc, ep) => {
      acc[ep.id] = {
        name: ep.name,
        id: ep.id
      };
      return acc;
    }, {});

    // Step 3: Generate the mapping logic using pattern matching
    const mappingLogic = {};
    entryPointData.forEach(ep => {
      // Find matching queue by removing "_EP" and finding corresponding "_Q"
      const baseName = ep.name.replace(/_EP$/, '');
      const matchingQueue = queueData.find(q => q.name === baseName + '_Q' || q.name === baseName);
      
      if (matchingQueue) {
        mappingLogic[ep.id] = {
          epName: ep.name,
          queue_id: matchingQueue.id,
          queueName: matchingQueue.name
        };
      }
    });

    const multiStepPrompt = `# Multi-Step Entry Point to Queue Mapping

## Step 1: Queue Data Object
Copy and paste this complete queue object into your function:

const queueData = ${JSON.stringify(queueObject, null, 2)};


## Step 2: Entry Point Data Object  
Copy and paste this complete entry point object into your function:


const entryPointData = ${JSON.stringify(entryPointObject, null, 2)};


## Step 3: Pre-Built Mapping Object
I've created the complete mapping using intelligent pattern matching:


const epMapping = ${JSON.stringify(mappingLogic, null, 2)};


## Step 4: Complete Function Template
Now generate a function that uses these objects:


export const handle = async (request, response) => {
    // Paste the three objects above here, then add:
    
    const entryPointId = request.inputs.entryPointId;
    
    if (epMapping[entryPointId]) {
        response.data = epMapping[entryPointId];
    } else {
        response.data = { 
            error: "Entry Point ID not found", 
            entryPointId: entryPointId 
        };
    }
    
    return response;
};


## Summary:
- **${queueData.length} Queues** processed
- **${entryPointData.length} Entry Points** processed  
- **${Object.keys(mappingLogic).length} Successful Mappings** created
- **${entryPointData.length - Object.keys(mappingLogic).length} Unmatched Entry Points**

Simply copy each code block into your function in the order shown!`;

    setLLMPrompt(multiStepPrompt);
    updateProgress("success", `Multi-step objects generated: ${Object.keys(mappingLogic).length}/${entryPointData.length} mappings created automatically!`);
  };

  const analyzeGeneratedCode = (code) => {
    const inputs = [];
    const outputExample = {};
    
    try {
      // Clean the code by removing markdown backticks if present
      let cleanCode = code;
      if (cleanCode.includes('```javascript') || cleanCode.includes('```js')) {
        cleanCode = cleanCode.replace(/```javascript\n|```js\n|```\n|```$/g, '');
      }
      
      // Extract input variables from request.inputs.variableName patterns
      const inputMatches = cleanCode.match(/request\.inputs\.(\w+)/g);
      if (inputMatches) {
        const uniqueInputs = [...new Set(inputMatches.map(match => match.replace('request.inputs.', '')))];
        uniqueInputs.forEach(inputName => {
          inputs.push({
            name: inputName,
            dataType: "STRING",
            description: "Input value for processing.",
            defaultValue: ""
          });
        });
      }
      
      // Extract output structure from response.data assignments
      const responseDataMatches = cleanCode.match(/response\.data\s*=\s*{([^}]+)}/g);
      if (responseDataMatches) {
        responseDataMatches.forEach(match => {
          // Extract the object content
          const objectContent = match.match(/{([^}]+)}/);
          if (objectContent) {
            const content = objectContent[1];
            // Parse key-value pairs
            const pairs = content.split(',').map(pair => pair.trim());
            pairs.forEach(pair => {
              if (pair.includes(':')) {
                const [key, value] = pair.split(':').map(s => s.trim());
                const cleanKey = key.replace(/["']/g, '');
                
                // Generate intelligent example values based on variable names
                let exampleValue;
                if (cleanKey.toLowerCase().includes('date') || cleanKey.toLowerCase().includes('time')) {
                  exampleValue = "2023-12-01";
                } else if (cleanKey.toLowerCase().includes('formatted')) {
                  exampleValue = "29/04/1990";
                } else if (cleanKey.toLowerCase().includes('id')) {
                  exampleValue = "12345";
                } else if (cleanKey.toLowerCase().includes('name')) {
                  exampleValue = "example_name";
                } else if (cleanKey.toLowerCase().includes('type')) {
                  exampleValue = "example_type";
                } else if (cleanKey.toLowerCase().includes('error')) {
                  exampleValue = "Error description";
                } else if (cleanKey.toLowerCase().includes('message') || cleanKey.toLowerCase().includes('result')) {
                  exampleValue = "Operation completed successfully";
                } else {
                  exampleValue = "result";
                }
                
                outputExample[cleanKey] = exampleValue;
              }
            });
          }
        });
      }
      
      // If no specific outputs found, create a generic one
      if (Object.keys(outputExample).length === 0) {
        outputExample.processedValue = "result";
        outputExample.timestamp = "ISO string";
      }
      
    } catch (error) {
      console.warn("Code analysis failed, using defaults:", error);
      // Fallback to defaults
      inputs.push({
        name: "inputValue",
        dataType: "STRING",
        description: "Input value for processing.",
        defaultValue: ""
      });
      outputExample.processedValue = "result";
      outputExample.timestamp = "ISO string";
    }
    
    return {
      inputs: inputs.length > 0 ? inputs : [{
        name: "inputValue",
        dataType: "STRING", 
        description: "Input value for processing.",
        defaultValue: ""
      }],
      outputExample
    };
  };

  const generateMappingJson = () => {
    if (!functionNameInput.trim()) {
      updateProgress("error", "Function Name cannot be empty.");
      return;
    }

    updateProgress("info", "Generating mapping JSON...");
    const entityMapping = {};
    // Create mapping from all selected entities across types
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      
      typeEntities.forEach((entity) => {
        if (typeSelections[entity.id]) {
          entityMapping[entity.name] = {
            [`${entityType.slice(0, -1)}_id`]: entity.id,
            entity_type: entityType
          };
        }
      });
    });

    const functionName = functionNameInput.trim();
    
    // Always use the sourceCode from the editor - this gives users full control
    let finalSourceCode = sourceCode;
    
    // Clean the source code by removing markdown backticks if present
    if (finalSourceCode.includes('```javascript') || finalSourceCode.includes('```js')) {
      finalSourceCode = finalSourceCode.replace(/```javascript\n|```js\n|```\n|```$/g, '');
    }
    
    // If sourceCode is still the default template and we're on mapping tab, generate mapping code
    if (activeTab === 'mapping' && sourceCode.includes('// Your JavaScript code here')) {
      const entityTypesList = selectedEntityTypes.map(t => entityTypes[t].label).join(', ');
      finalSourceCode = `export const handle = async (request, response) => {
    // Do not remove the existing wrapper code, else your code might not execute properly

    // Get the entity name from the input
    const entityName = request.inputs.entityName;

    // JSON mapping entity names to their respective IDs and types
    // Supports: ${entityTypesList}
    const entityMapping = ${JSON.stringify(entityMapping, null, 4)};

    // Check if the entity exists in our mapping
    if (entityMapping[entityName]) {
        // If entity is found, return its specific ID and type
        response.data = entityMapping[entityName];
    } else {
        // Handle case where entity is not found
        response.data = { error: "Entity not found in mapping", available_types: "${entityTypesList}" };
    }

    // Return the response object
    return response;
};`;
      // Update the editor with the generated code
      setSourceCode(finalSourceCode);
    } else {
      // Clean the final source code again to ensure no markdown backticks in JSON output
      if (finalSourceCode.includes('```javascript') || finalSourceCode.includes('```js')) {
        finalSourceCode = finalSourceCode.replace(/```javascript\n|```js\n|```\n|```$/g, '');
      }
    }

    // Analyze the generated code to extract inputs and outputs
    const codeAnalysis = analyzeGeneratedCode(finalSourceCode);
    
    const jsonOutput = {
      name: functionName,
      language: "js",
      runtime: "nodejs22.x",
      description: "Custom JavaScript function created with Enhanced WxCC Function Generator.",
      sourceCode: finalSourceCode,
      inputs: activeTab === 'mapping' && sourceCode.includes('// Your JavaScript code here') 
        ? [{ name: "entityName", dataType: "STRING", description: "The name of the entity to look up across all mapped entity types.", defaultValue: "" }]
        : codeAnalysis.inputs,
      outputs: activeTab === 'mapping' && sourceCode.includes('// Your JavaScript code here')
        ? '{"entity_id": "entity id value", "entity_type": "type of entity"}'
        : JSON.stringify(codeAnalysis.outputExample),
    };

    setGeneratedJson(jsonOutput);
    updateProgress("success", "Function JSON generated successfully.");
  };

  const handleDownloadJson = () => {
    if (generatedJson) {
      const fileName = `${generatedJson.name}_Function.json`;
      const blob = new Blob([JSON.stringify(generatedJson, null, 2)], {
        type: 'application/json',
      });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      updateProgress("success", `Downloaded ${fileName}`);
    } else {
      updateProgress("warning", "No JSON to download. Generate it first.");
    }
  };

  const generateCompleteDataArrays = () => {
    updateProgress("info", "Generating complete data arrays for all selected entities...");
    
    let completeArraysText = "// COMPLETE DATA ARRAYS - COPY-PASTE INTO LLM TEMPLATE\n\n";
    let totalEntities = 0;
    
    selectedEntityTypes.forEach(entityType => {
      const typeEntities = allEntities[entityType] || [];
      const typeSelections = selectedEntities[entityType] || {};
      const selectedEntityData = typeEntities.filter(entity => typeSelections[entity.id]);
      
      if (selectedEntityData.length === 0) return;
      
      totalEntities += selectedEntityData.length;
      const essentialData = selectedEntityData.map(e => ({ id: e.id, name: e.name }));
      
      // Create proper variable name
      const varName = entityType === 'queues' ? 'queueData' : 
                     entityType === 'entrypoints' ? 'entryPointData' :
                     entityType === 'businesshours' ? 'businessHoursData' :
                     entityType === 'cadvar' ? 'cadvarData' :
                     entityType === 'skilldefinitions' ? 'skillDefinitionsData' :
                     `${entityType}Data`;
      
      completeArraysText += `const ${varName} = [\n`;
      essentialData.forEach((entity, index) => {
        const comma = index < essentialData.length - 1 ? ',' : '';
        completeArraysText += `  { "id": "${entity.id}", "name": "${entity.name}" }${comma}\n`;
      });
      completeArraysText += `];\n\n`;
    });
    
    completeArraysText += `// Total entities: ${totalEntities}\n`;
    completeArraysText += `// Instructions:\n`;
    completeArraysText += `// 1. Copy the arrays above\n`;
    completeArraysText += `// 2. Paste them into the LLM-generated template where indicated\n`;
    completeArraysText += `// 3. The LLM will provide the main function that uses these arrays\n\n`;
    
    // Show the complete arrays in a modal/popup style display
    const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    popup.document.write(`
      <html>
        <head>
          <title>Complete Data Arrays</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 20px; background: #f5f5f5; }
            .container { background: white; padding: 20px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
            pre { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 15px; overflow-x: auto; }
            .header { color: #28a745; margin-bottom: 20px; font-size: 18px; font-weight: bold; }
            .copy-btn { background: #28a745; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; margin-bottom: 15px; }
            .copy-btn:hover { background: #218838; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">Complete Data Arrays (${totalEntities} entities)</div>
            <button class="copy-btn" onclick="navigator.clipboard.writeText(document.getElementById('code').textContent).then(() => alert('Copied to clipboard!'))">📋 Copy All Arrays</button>
            <pre id="code">${completeArraysText.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
            <p><strong>Next Steps:</strong></p>
            <ol>
              <li>Copy the arrays above using the button</li>
              <li>Go back to the Function Creator and click "Generate Function JSON"</li>
              <li>The LLM will provide a template with placeholders</li>
              <li>Paste these arrays into the template where indicated</li>
            </ol>
          </div>
        </body>
      </html>
    `);
    
    updateProgress("success", `Generated complete data arrays for ${totalEntities} entities. Check the popup window.`);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: 'auto', fontFamily: 'Inter, sans-serif', backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', textAlign: 'center' }}>
        <h1 style={{ color: '#333', marginBottom: '10px', fontSize: '2.2em' }}>Enhanced WxCC Function Generator</h1>
        <p style={{ color: '#666', marginBottom: '15px', fontSize: '1.1em' }}>
          Generate dynamic AWS Lambda functions for Webex Contact Center with AI assistance
        </p>
        <p style={{ color: '#888', fontSize: '0.9em' }}>
          Authenticated with Org ID: <span style={{ fontWeight: 'bold', fontFamily: 'monospace', backgroundColor: '#e9ecef', padding: '2px 6px', borderRadius: '3px' }}>{authData.org_id}</span>
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff', padding: '5px' }}>
        <div style={{ display: 'flex', gap: '5px' }}>
          <button
            onClick={() => setActiveTab('mapping')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1em',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              backgroundColor: activeTab === 'mapping' ? '#007bff' : 'transparent',
              color: activeTab === 'mapping' ? 'white' : '#666'
            }}
          >
            <Settings size={18} />
            <span>Entity Mapping</span>
          </button>
          <button
            onClick={() => setActiveTab('llm')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1em',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              backgroundColor: activeTab === 'llm' ? '#6f42c1' : 'transparent',
              color: activeTab === 'llm' ? 'white' : '#666'
            }}
          >
            <Bot size={18} />
            <span>AI Function Generator</span>
          </button>
        </div>
      </div>

      {/* Function Configuration */}
      <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
        <h2 style={{ fontSize: '1.3em', marginBottom: '15px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Code size={20} />
          <span>Function Configuration</span>
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'mapping' ? '1fr 1fr' : '1fr', gap: '15px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>Function Name</label>
            <input
              type="text"
              value={functionNameInput}
              onChange={(e) => setFunctionNameInput(e.target.value)}
              placeholder="e.g., MyEnhancedFunction"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '1em'
              }}
            />
          </div>
          
          {activeTab === 'mapping' && (
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>Entity Types (Select Multiple)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: '1px solid #ccc', borderRadius: '5px', padding: '10px', backgroundColor: '#f8f9fa' }}>
                {Object.entries(entityTypes).map(([key, type]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedEntityTypes.includes(key)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEntityTypes(prev => [...prev, key]);
                        } else {
                          setSelectedEntityTypes(prev => prev.filter(t => t !== key));
                        }
                      }}
                      style={{ transform: 'scale(1.1)' }}
                    />
                    <span>{type.label}</span>
                    {loadingEntityTypes.has(key) && (
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: '2px solid #007bff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                    )}
                  </label>
                ))}
              </div>
              <div style={{ marginTop: '8px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', color: '#444', fontSize: '0.9em' }}>Currently Viewing:</label>
                <select
                  value={currentEntityType}
                  onChange={(e) => setCurrentEntityType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '0.9em'
                  }}
                >
                  {selectedEntityTypes.map(key => (
                    <option key={key} value={key}>
                      📋 {entityTypes[key].label} ({allEntities[key]?.length || 0} items)
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {activeTab === 'llm' && (
          <div style={{ display: 'grid', gap: '15px' }}>
            <div style={{ padding: '12px 14px', backgroundColor: '#f0f7ff', borderRadius: '8px', border: '1px solid #b6dbff', fontSize: '0.92em', color: '#1a3a6a', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span role="img" aria-label="bedrock">🪨</span>
              <span>Generated by <strong>Amazon Bedrock — Claude Sonnet 4.6</strong>. No API key needed; your sign-in token authorizes the request.</span>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>
                Function Prompt
              </label>
              <textarea
                value={llmPrompt}
                onChange={(e) => setLLMPrompt(e.target.value)}
                placeholder="Describe your function in plain English. For example: 'Create a function that maps Entry Point IDs to Queue information' or 'Build a lookup that takes a department code and returns all related data'..."
                rows={6}
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #6f42c1',
                  borderRadius: '8px',
                  fontSize: '1.1em',
                  resize: 'vertical',
                  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                  lineHeight: '1.5',
                  boxShadow: '0 2px 8px rgba(111, 66, 193, 0.1)'
                }}
              />
            </div>

            {/* Simplified - Removed helper buttons as requested */}
            <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '6px', fontSize: '0.9em', color: '#0066cc' }}>
              💡 <strong>Pro Tip:</strong> Describe your vision in plain English. The AI will automatically handle large datasets, create optimal data structures, and provide step-by-step instructions if needed.
            </div>

            {/* Entity Relationships Display */}
            {entityRelationships.length > 0 && (
              <div style={{ 
                marginBottom: '15px', 
                padding: '15px', 
                backgroundColor: '#e7f3ff', 
                borderRadius: '8px',
                border: '1px solid #007bff'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#0066cc' }}>🔗 Detected Entity Relationships</h4>
                <div style={{ fontSize: '0.9em', color: '#333' }}>
                  {entityRelationships.slice(0, 5).map(([baseName, rel], index) => (
                    <div key={baseName} style={{ marginBottom: '5px', padding: '5px', backgroundColor: '#fff', borderRadius: '4px' }}>
                      <strong>{baseName}</strong>: 
                      {rel.entryPoint && <span style={{ color: '#28a745', marginLeft: '5px' }}>EP ✓</span>}
                      {rel.queue && <span style={{ color: '#007bff', marginLeft: '5px' }}>Queue ✓</span>}
                      {rel.ccbStatus && <span style={{ color: '#6f42c1', marginLeft: '5px' }}>CCB ✓</span>}
                      <span style={{ color: '#666', marginLeft: '8px', fontSize: '0.8em' }}>({Math.round(rel.confidence * 100)}% match)</span>
                    </div>
                  ))}
                  {entityRelationships.length > 5 && (
                    <div style={{ fontSize: '0.8em', color: '#666', fontStyle: 'italic' }}>
                      ...and {entityRelationships.length - 5} more relationships
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Simplified AI Generation */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => generateIntelligentLLMFunction()}
                disabled={isLLMGenerating || !llmPrompt.trim() || !backendConfigured}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '15px 30px',
                  backgroundColor: isLLMGenerating || !llmPrompt.trim() || !backendConfigured ? '#ccc' : '#6f42c1',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isLLMGenerating || !llmPrompt.trim() || !backendConfigured ? 'not-allowed' : 'pointer',
                  fontSize: '1.1em',
                  fontWeight: '600',
                  transition: 'background-color 0.3s ease',
                  boxShadow: '0 4px 12px rgba(111, 66, 193, 0.3)'
                }}
              >
                {isLLMGenerating ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '3px solid #fff',
                      borderTop: '3px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Creating Your Function...</span>
                  </>
                ) : (
                  <>
                    <Bot size={20} />
                    <span>🤖 Generate Intelligent Function</span>
                  </>
                )}
              </button>
            </div>

            {/* Quality-judge advisory result panel — appears AFTER generation. */}
            {(judgeEvaluating || typeof qualityScore === 'number' || qualityFindings.length > 0) && (
              <div style={{
                marginTop: '4px',
                padding: '12px 14px',
                borderRadius: '8px',
                border: '1px solid',
                borderColor:
                  judgeEvaluating ? '#cbd5e0' :
                  typeof qualityScore === 'number' && qualityScore >= 0.8 ? '#86efac' :
                  typeof qualityScore === 'number' && qualityScore >= 0.6 ? '#fcd34d' :
                  '#fca5a5',
                backgroundColor:
                  judgeEvaluating ? '#f7fafc' :
                  typeof qualityScore === 'number' && qualityScore >= 0.8 ? '#f0fdf4' :
                  typeof qualityScore === 'number' && qualityScore >= 0.6 ? '#fffbeb' :
                  '#fef2f2'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span role="img" aria-label="quality">🛡️</span>
                    {judgeEvaluating ? (
                      <span style={{ fontWeight: 600 }}>Quality-judging your generated function…</span>
                    ) : typeof qualityScore === 'number' ? (
                      <span style={{ fontWeight: 600 }}>
                        Quality grade: {Math.round(qualityScore * 100)}%
                        {qualityFindings.length > 0 ? ` · ${qualityFindings.length} finding${qualityFindings.length === 1 ? '' : 's'}` : ' · no findings'}
                      </span>
                    ) : null}
                  </div>
                  {!judgeEvaluating && qualityFindings.filter((f) => f && f.severity !== 'info').length > 0 && (
                    <button
                      onClick={() => handleRegenerateWithFindings(qualityFindings.filter((f) => f && f.severity !== 'info'))}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9em',
                        fontWeight: 600
                      }}
                    >
                      🔄 Regenerate addressing {qualityFindings.filter((f) => f && f.severity !== 'info').length} finding{qualityFindings.filter((f) => f && f.severity !== 'info').length === 1 ? '' : 's'}
                    </button>
                  )}
                </div>
                {qualityFindings.length > 0 && (
                  <div style={{ marginTop: '10px', display: 'grid', gap: '6px' }}>
                    {qualityFindings.map((f, i) => (
                      <div key={i} style={{ fontSize: '0.85em', lineHeight: 1.4 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '1px 6px',
                          marginRight: '8px',
                          borderRadius: '4px',
                          fontSize: '0.78em',
                          fontWeight: 600,
                          color: 'white',
                          backgroundColor: f.severity === 'error' ? '#dc2626' : f.severity === 'warn' ? '#d97706' : '#6b7280'
                        }}>{f.severity || 'info'}</span>
                        <strong>{f.itemId}:</strong> {f.reason}{f.suggestion ? ` — ${f.suggestion}` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* JavaScript Source Code Editor */}
            <div style={{ marginTop: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ fontWeight: '600', color: '#444' }}>
                  📝 JavaScript Source Code
                </label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setSourceCode(`export const handle = async (request, response) => {
  // Your JavaScript code here
  // Access inputs via request.inputs
  // Set outputs via response.data = {...}
  
  // Example:
  const inputValue = request.inputs.inputName;
  
  response.data = {
    message: "Hello from your custom function!",
    input: inputValue
  };
  
  return response;
};`)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8em'
                    }}
                  >
                    Reset Template
                  </button>
                  {llmGeneratedCode && (
                    <button
                      onClick={() => setSourceCode(llmGeneratedCode)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      Use Generated
                    </button>
                  )}
                  <button
                    onClick={() => setSourceCode(`export const handle = async (request, response) => {
  try {
    // Get the queue entry point ID from the request
    const queueEntryPointId = request.inputs.queueEntryPointId;

    if (!queueEntryPointId) {
      response.statusCode = 400;
      response.data = { error: "Queue Entry Point ID is required." };
      return response;
    }

    // Find the entry point by ID
    const entryPoint = entryPointData.find((ep) => ep.id === queueEntryPointId);

    if (!entryPoint) {
      response.statusCode = 404;
      response.data = { error: "Queue Entry Point not found." };
      return response;
    }

    const entryPointName = entryPoint.name;

    // Clean the entry point name by removing the _EP suffix
    // This is the correct logic for your data structure
    const cleanedEntryPointName = entryPointName.replace(/_EP$/, '').toLowerCase();

    // Find the corresponding queue that has a name matching the cleaned entry point name
    const matchedQueue = queueData.find(queue => {
      // The queue names are already correct for direct comparison with the cleaned entry point name.
      // No extra cleaning is needed on the queue name.
      return cleanedEntryPointName === queue.name.toLowerCase();
    });

    if (!matchedQueue) {
      response.statusCode = 404;
      response.data = { error: "No matching queue found for the entry point." };
      return response;
    }

    const mapping = {
      epName: entryPointName,
      queue_id: matchedQueue.id,
      queueName: matchedQueue.name,
    };

    response.data = mapping;
    response.statusCode = 200;

  } catch (error) {
    console.error("Error processing request:", error);
    response.statusCode = 500;
    response.data = { error: "Internal server error.", details: error.message };
  }

  return response;
};`)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#6f42c1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.8em'
                    }}
                  >
                    ✨ Best Practice Template
                  </button>
                </div>
              </div>
              <textarea
                value={sourceCode}
                onChange={(e) => setSourceCode(e.target.value)}
                placeholder="Edit your JavaScript function code here..."
                rows={15}
                style={{
                  width: '100%',
                  padding: '15px',
                  border: '2px solid #28a745',
                  borderRadius: '8px',
                  fontSize: '0.9em',
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  resize: 'vertical',
                  lineHeight: '1.4',
                  backgroundColor: '#f8f9fa',
                  color: '#333'
                }}
              />
              <small style={{ color: '#666', fontSize: '0.8em', marginTop: '5px', display: 'block' }}>
                💡 Edit your function directly here. This will be the exact code used in the downloaded JSON file.
              </small>
            </div>
          </div>
        )}
      </div>

      {/* Entity Selection (only for mapping tab) */}
      {activeTab === 'mapping' && (
        <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2 style={{ fontSize: '1.3em', margin: 0, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckSquare size={20} />
              <span>Select {entityTypes[currentEntityType].label}</span>
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.85em', color: '#666' }}>
              {(() => {
                const totalSelected = selectedEntityTypes.reduce((total, type) => {
                  const typeSelections = selectedEntities[type] || {};
                  return total + Object.keys(typeSelections).filter(id => typeSelections[id]).length;
                }, 0);
                
                return totalSelected > 0 && (
                  <span style={{ 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '12px',
                    fontSize: '0.8em'
                  }}>
                    {totalSelected} selected for AI across {selectedEntityTypes.length} types
                  </span>
                );
              })()}
              <span>💡 Selected entities will be included in AI function generation</span>
            </div>
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px', color: '#007bff' }}>
              <div style={{
                width: '24px',
                height: '24px',
                border: '3px solid #007bff',
                borderTop: '3px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '10px'
              }}></div>
              <span>Loading entities...</span>
            </div>
          ) : (
            <>
              {/* Enhanced Controls with Filtering */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  onClick={handleSelectAllFiltered}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '8px 15px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <CheckSquare size={16} />
                  <span>Select All {filteredEntities.length < currentEntities.length ? 'Filtered' : ''} ({filteredEntities.length})</span>
                </button>
                <button
                  onClick={handleDeselectAllFiltered}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '8px 15px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <Square size={16} />
                  <span>Deselect All {filteredEntities.length < currentEntities.length ? 'Filtered' : ''}</span>
                </button>
                
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '8px 15px',
                    backgroundColor: showFilters ? '#28a745' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    transition: 'background-color 0.3s ease'
                  }}
                >
                  <Settings size={16} />
                  <span>Advanced Filters {filters.length > 0 && `(${filters.length})`}</span>
                </button>

                {filters.length > 0 && (
                  <button
                    onClick={clearAllFilters}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '8px 15px',
                      backgroundColor: '#ffc107',
                      color: '#212529',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <span>🗑️ Clear Filters</span>
                  </button>
                )}
              </div>

              {/* Advanced Filters Panel */}
              {showFilters && (
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '15px', 
                  backgroundColor: '#f8f9fa', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '6px' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, color: '#495057' }}>🔍 Advanced Filters</h4>
                    <button
                      onClick={addFilter}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8em'
                      }}
                    >
                      + Add Filter
                    </button>
                  </div>
                  
                  {filters.map((filter, index) => (
                    <div key={filter.id} style={{ 
                      display: 'flex', 
                      gap: '10px', 
                      alignItems: 'center', 
                      marginBottom: '8px',
                      padding: '8px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #dee2e6'
                    }}>
                      <select
                        value={filter.field}
                        onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '0.8em'
                        }}
                      >
                        <option value="name">Name</option>
                        <option value="id">ID</option>
                      </select>
                      
                      <select
                        value={filter.type}
                        onChange={(e) => updateFilter(filter.id, 'type', e.target.value)}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '0.8em',
                          minWidth: '100px'
                        }}
                      >
                        <option value="startsWith">Starts with</option>
                        <option value="endsWith">Ends with</option>
                        <option value="contains">Contains</option>
                        <option value="notContains">Not contains</option>
                        <option value="equals">Equals</option>
                        <option value="regex">Regex</option>
                      </select>
                      
                      <input
                        type="text"
                        value={filter.value}
                        onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                        placeholder={`Enter ${filter.type === 'regex' ? 'regex pattern' : 'value'}`}
                        style={{
                          flex: 1,
                          padding: '4px 8px',
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '0.8em'
                        }}
                      />
                      
                      <button
                        onClick={() => removeFilter(filter.id)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '0.8em'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {filters.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#6c757d', 
                      fontStyle: 'italic',
                      padding: '10px'
                    }}>
                      No filters added yet. Click "+ Add Filter" to create your first filter.
                    </div>
                  )}
                  
                  {filters.length > 0 && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '8px', 
                      backgroundColor: '#e7f3ff',
                      borderRadius: '4px',
                      fontSize: '0.8em',
                      color: '#0066cc'
                    }}>
                      💡 Example: Name "starts with" → "AO_" AND Name "ends with" → "_EP" would find "AO_Something_EP"
                    </div>
                  )}
                </div>
              )}

              {/* Basic Search */}
              <div style={{ position: 'relative', flexGrow: 1, minWidth: '200px' }}>
                <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    paddingLeft: '35px',
                    paddingRight: '10px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '5px',
                    fontSize: '0.9em'
                  }}
                />
              </div>

              {/* Entity List */}
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '5px' }}>
                {filteredEntities.map((entity) => (
                  <div key={entity.id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    padding: '12px', 
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      id={`entity-${entity.id}`}
                      checked={!!selectedEntities[currentEntityType]?.[entity.id]}
                      onChange={() => handleEntityToggle(entity.id)}
                      style={{ marginRight: '12px', transform: 'scale(1.2)' }}
                    />
                    <label htmlFor={`entity-${entity.id}`} style={{ cursor: 'pointer', flexGrow: 1 }}>
                      <span style={{ fontWeight: '500', color: '#333' }}>{entity.name}</span>
                      <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '8px' }}>({entity.id})</span>
                    </label>
                  </div>
                ))}
                
                {filteredEntities.length === 0 && searchTerm && (
                  <div style={{ padding: '30px', textAlign: 'center', color: '#888' }}>
                    No {entityTypes[currentEntityType].label.toLowerCase()} match your search.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
        <button
          onClick={generateMappingJson}
          disabled={loading || (activeTab === 'mapping' && selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0)) || !functionNameInput.trim()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 25px',
            backgroundColor: loading || (activeTab === 'mapping' && selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0)) || !functionNameInput.trim() ? '#ccc' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: loading || (activeTab === 'mapping' && selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0)) || !functionNameInput.trim() ? 'not-allowed' : 'pointer',
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease',
            boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)'
          }}
        >
          <Zap size={18} />
          <span>Generate Function JSON</span>
        </button>

        <button
          onClick={handleDownloadJson}
          disabled={!generatedJson}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 25px',
            backgroundColor: !generatedJson ? '#ccc' : '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: !generatedJson ? 'not-allowed' : 'pointer',
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease',
            boxShadow: '0 2px 8px rgba(23, 162, 184, 0.2)'
          }}
        >
          <Download size={18} />
          <span>Download JSON</span>
        </button>

        <button
          onClick={generateCompleteDataArrays}
          disabled={selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 25px',
            backgroundColor: selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0) ? '#ccc' : '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: selectedEntityTypes.every(type => Object.keys(selectedEntities[type] || {}).filter(id => selectedEntities[type][id]).length === 0) ? 'not-allowed' : 'pointer',
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease',
            boxShadow: '0 2px 8px rgba(111, 66, 193, 0.2)'
          }}
        >
          <span>📄</span>
          <span>Generate Complete Data Arrays</span>
        </button>
      </div>

      {/* Progress Log */}
      <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
        <h2 style={{ fontSize: '1.2em', marginBottom: '15px', color: '#333' }}>Progress Log</h2>
        <div
          ref={logContainerRef}
          style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '6px',
            height: '200px',
            overflowY: 'auto',
            fontSize: '0.9em',
            border: '1px solid #e9ecef'
          }}
        >
          {progress.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>No activity yet...</p>
          ) : (
            <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
              {progress.map((item, index) => (
                <li key={index} style={{ 
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px'
                }}>
                  <span style={{ 
                    color: '#999', 
                    fontSize: '0.8em', 
                    fontFamily: 'monospace',
                    flexShrink: 0
                  }}>
                    [{item.timestamp}]
                  </span>
                  <span style={getMessageStyle(item.type)}>
                    {item.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
        <button
          onClick={() => setAdvancedSettings(!advancedSettings)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: 0,
            border: 'none',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            fontSize: '1.2em',
            fontWeight: '600',
            color: '#333'
          }}
        >
          <span>Advanced Settings</span>
          {advancedSettings ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
        
        {advancedSettings && (
          <div style={{ marginTop: '15px', display: 'grid', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>
                Runtime Version
              </label>
              <select style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '5px',
                fontSize: '1em'
              }}>
                <option value="nodejs22.x">Node.js 22.x</option>
                <option value="nodejs20.x">Node.js 20.x</option>
                <option value="nodejs18.x">Node.js 18.x</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#444' }}>
                Timeout (seconds)
              </label>
              <input
                type="number"
                defaultValue={30}
                min={1}
                max={900}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  fontSize: '1em'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Generated JSON Display */}
      {generatedJson && (
        <div style={{ marginBottom: '25px', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', backgroundColor: '#fff' }}>
          <h2 style={{ fontSize: '1.3em', marginBottom: '15px', color: '#333' }}>Generated Function JSON</h2>
          <pre style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '6px',
            fontSize: '0.85em',
            overflowX: 'auto',
            border: '1px solid #e9ecef',
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>
            <code>{JSON.stringify(generatedJson, null, 2)}</code>
          </pre>

          <div style={{ 
            marginTop: '20px', 
            padding: '15px', 
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb', 
            borderRadius: '6px' 
          }}>
            <h3 style={{ color: '#155724', marginBottom: '10px', fontSize: '1.1em' }}>
              📋 Installation Instructions
            </h3>
            <ol style={{ color: '#155724', lineHeight: '1.6', paddingLeft: '20px', margin: 0 }}>
              <li><strong>Import Function:</strong> In Webex Control Hub → Contact Center → Developer → Functions → Create Function → Import Function</li>
              <li><strong>Upload JSON:</strong> Upload the downloaded JSON file</li>
              <li><strong>Add to Flow:</strong> Drag a "Function" node into your call flow</li>
              <li><strong>Configure:</strong> Select your function and set input/output variables</li>
              <li><strong>Test:</strong> Validate the function in your flow</li>
            </ol>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={onBackToTools}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.9em',
            transition: 'background-color 0.3s ease'
          }}
        >
          ← Back to Tools
        </button>
      </div>

      {/* Add CSS animation for spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via judgeEvaluating flag */ }}
        kind="wxcc-function"
        color="violet"
      />
    </div>
  );
};

export default EnhancedQueueMapping;
